import { useEffect, useRef, useState } from "react";
import { TERM_WORDS } from "../data/termWords.js";
import {
  EMPTY_TERM_ROW,
  normalizeTermWord,
  solvedTermCount,
} from "../domain/term.js";

export function useTermEngine({
  secrets,
  maxAttempts,
  resetKey,
  initialGuesses = [],
  initialFinished = false,
  onFinish,
}) {
  const [guesses, setGuesses] = useState(initialGuesses);
  const [letters, setLetters] = useState(EMPTY_TERM_ROW);
  const lettersRef = useRef(EMPTY_TERM_ROW());
  const [activeIndex, setActiveIndex] = useState(0);
  const activeRef = useRef(0);
  const selectedRef = useRef(null);
  const [error, setError] = useState("");
  const [finished, setFinished] = useState(initialFinished);
  const [won, setWon] = useState(
    initialFinished &&
      solvedTermCount(secrets, initialGuesses) === secrets.length,
  );
  const finishedRef = useRef(initialFinished);
  const onFinishRef = useRef(onFinish);
  const mountedRef = useRef(false);
  onFinishRef.current = onFinish;

  const clearLetters = () => {
    const empty = EMPTY_TERM_ROW();
    lettersRef.current = empty;
    activeRef.current = 0;
    selectedRef.current = null;
    setLetters(empty);
    setActiveIndex(0);
  };

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    setGuesses([]);
    setFinished(false);
    setWon(false);
    finishedRef.current = false;
    setError("");
    clearLetters();
  }, [resetKey]);

  const finish = (victory, attemptCount = guesses.length) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinished(true);
    setWon(victory);
    onFinishRef.current?.(victory, attemptCount);
  };

  const submit = () => {
    const raw = lettersRef.current.join("");
    if (raw.length < 5) {
      setError("Preencha as 5 letras!");
      window.setTimeout(() => setError(""), 2000);
      return;
    }
    const guess = normalizeTermWord(raw);
    const isSecret = secrets.some(
      (secret) => normalizeTermWord(secret) === guess,
    );
    if (!TERM_WORDS.includes(guess) && !isSecret) {
      setError("Essa palavra não existe!");
      window.setTimeout(() => setError(""), 2000);
      return;
    }
    const next = [...guesses, guess];
    setGuesses(next);
    clearLetters();
    setError("");
    if (solvedTermCount(secrets, next) === secrets.length)
      finish(true, next.length);
    else if (next.length >= maxAttempts) finish(false, next.length);
  };

  const type = (key) => {
    if (finishedRef.current) return;
    setError("");
    if (key === "ENTER") return submit();
    if (key === "BACKSPACE") {
      let index = activeRef.current;
      const next = [...lettersRef.current];
      if (selectedRef.current === null) {
        if (next[index] === "" && index > 0) {
          index -= 1;
          next[index] = "";
        } else next[index] = "";
      } else {
        index = selectedRef.current;
        next[index] = "";
        selectedRef.current = null;
      }
      lettersRef.current = next;
      activeRef.current = index;
      setLetters(next);
      setActiveIndex(index);
      return;
    }
    if (!/^[A-Z]$/.test(key)) return;
    const next = [...lettersRef.current];
    const selected = selectedRef.current;
    const index =
      selected === null ? next.findIndex((value) => value === "") : selected;
    if (index === -1) return;
    next[index] = key;
    selectedRef.current = null;
    const emptyIndex = next.findIndex((value) => value === "");
    const active = emptyIndex === -1 ? 4 : emptyIndex;
    lettersRef.current = next;
    activeRef.current = active;
    setLetters(next);
    setActiveIndex(active);
  };

  const selectCell = (index) => {
    if (finishedRef.current) return;
    selectedRef.current = index;
    activeRef.current = index;
    setActiveIndex(index);
  };

  useEffect(() => {
    const handleKey = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const key = event.key.toUpperCase();
      if (
        key === "BACKSPACE" ||
        key === "ENTER" ||
        (key.length === 1 && key >= "A" && key <= "Z")
      ) {
        event.preventDefault();
        type(key);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [guesses, maxAttempts, secrets]);

  return {
    guesses,
    letters,
    activeIndex,
    error,
    finished,
    won,
    type,
    selectCell,
    finish,
  };
}
